import type { BrowserBackend } from "../backend/contracts";

export type VersionedEntity = {
  id: string;
  version: number;
};

export type LocalResourceClientOptions = {
  origin?: string;
  beforeRequest?: (method: string) => Promise<void>;
};

export class LocalResourceError extends Error {
  constructor(
    public readonly code: string,
    public readonly status: number,
    public readonly requestId: string | undefined,
    message: string,
  ) {
    super(message);
    this.name = "LocalResourceError";
  }
}

export class LocalResourceClient<
  TEntity extends VersionedEntity,
  TCreateInput extends object,
  TPatchInput extends object,
> {
  constructor(
    private readonly backend: Pick<BrowserBackend, "request">,
    private readonly collectionPath: string,
    options: LocalResourceClientOptions = {},
  ) {
    this.origin = options.origin ?? (typeof window === "undefined" ? "https://browser-local.invalid" : window.location.origin);
    this.beforeRequest = options.beforeRequest;
  }

  private readonly origin: string;
  private readonly beforeRequest?: (method: string) => Promise<void>;

  async list(): Promise<TEntity[]> {
    const result = await this.json<{ items: TEntity[] }>(this.request(this.collectionPath));
    return result.items;
  }

  async get(id: string): Promise<TEntity> {
    const result = await this.json<{ item: TEntity }>(this.request(`${this.collectionPath}/${encodeURIComponent(id)}`));
    return result.item;
  }

  async create(input: TCreateInput): Promise<TEntity> {
    const result = await this.json<{ item: TEntity }>(this.request(this.collectionPath, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input),
    }));
    return result.item;
  }

  async update(id: string, input: TPatchInput, version: number): Promise<TEntity> {
    const result = await this.json<{ item: TEntity }>(this.request(`${this.collectionPath}/${encodeURIComponent(id)}`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "If-Match": String(version),
      },
      body: JSON.stringify(input),
    }));
    return result.item;
  }

  async delete(id: string, version: number): Promise<void> {
    await this.requestResponse(this.request(`${this.collectionPath}/${encodeURIComponent(id)}`, {
      method: "DELETE",
      headers: { "If-Match": String(version) },
    }));
  }

  private request(path: string, init: RequestInit = {}): Request {
    return new Request(new URL(path, this.origin), init);
  }

  private async json<T>(request: Request): Promise<T> {
    const response = await this.requestResponse(request);
    return response.json() as Promise<T>;
  }

  private async requestResponse(request: Request): Promise<Response> {
    await this.beforeRequest?.(request.method);
    const response = await this.backend.request(request);
    if (response.ok) return response;

    const payload = await response.clone().json().catch(() => undefined) as {
      error?: { code?: string; message?: string; requestId?: string };
    } | undefined;
    throw new LocalResourceError(
      payload?.error?.code ?? `http_${response.status}`,
      response.status,
      payload?.error?.requestId,
      payload?.error?.message ?? `Request failed with ${response.status}.`,
    );
  }
}
